import {
	Box,
	Button,
	Text,
	Icons,
	Stack,
	Table,
	Container,
	Heading,
	Col,
	Grid,
	joinAttributes,
	Spinner,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Time,
	Money,
	Inline,
	MoneySum,
	TimeDuration,
	Badge,
} from "@sembark-travel/ui/base"
import {
	useSearch,
	TSearchParams,
	Search,
	ListView,
} from "@sembark-travel/ui/list"
import {
	Breadcrumbs,
	useLocationQuery,
	NavLink,
	ButtonLink,
	Link,
	XHRLink,
} from "@sembark-travel/ui/router"
import {
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	parseDateFromQuery,
} from "@sembark-travel/datetime-utils"
import { isNumeric, numberToLocalString } from "@sembark-travel/number-utils"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import { useEffect, useMemo } from "react"
import useSWR from "swr"
import { AccountsList } from "../Accounting"
import { ICabSchedule, IScheduledCab } from "../CabScheduling"
import config from "../config"
import {
	ContactsList,
	Email,
	IContact,
	PhoneNumber,
	SelectContact,
} from "../Contacts"
import { DriversList, TDriver } from "../Drivers"
import { PaymentsList } from "../Payments"
import { generatePath } from "./../router-utils"
import { ITransportServiceProvider, ITSPTripWiseCabsSchedule } from "./store"
import { UpdateItemInDialog } from "./UpdateItem"
import {
	DatePickerField,
	DateRangePickerField,
	SelectInputField,
} from "@sembark-travel/ui/form"
import { PERMISSIONS, useCheckPermissions, useHasFeatureFlag } from "../Auth"
import { TTravelActiviytBooking } from "../TravelActivityBookings"
import { getStartDateTimeOfCabSchedule } from "../CabScheduling/utils"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"
import { TemporaryStorage } from "../TemporaryStorage"
import {
	addMoneyFromDifferentCurrencies,
	moneyParseByDecimal,
	formatMoneyByIntl,
	TMoney,
} from "@sembark-travel/money"
import { ICab } from "../Cabs"

function XHR(xhr: XHRInstance) {
	return {
		async getTransportServiceProvider(
			id: string
		): Promise<ITransportServiceProvider> {
			return xhr
				.get(`/transport-service-providers/${id}`)
				.then((resp) => resp.data.data)
		},
		async getBookings(params: unknown): Promise<IListResponse<IScheduledCab>> {
			return xhr.get(`/scheduled-cabs`, { params }).then((resp) => resp.data)
		},
		async getTripWiseBookings(
			params: unknown
		): Promise<IListResponse<ITSPTripWiseCabsSchedule>> {
			return xhr
				.get(`/trip-wise-cab-schedule`, { params })
				.then((resp) => resp.data)
		},
		async getTravelActivityBookings(
			params: unknown
		): Promise<IListResponse<TTravelActiviytBooking>> {
			return xhr
				.get(`/travel-activity-bookings`, { params })
				.then((resp) => resp.data)
		},
		async createContact(
			id: string | number,
			contactData: object
		): Promise<IContact> {
			return xhr
				.post(`/transport-service-provider-contacts`, {
					transport_service_provider_id: id,
					...contactData,
				})
				.then((resp) => resp.data.data)
		},
	}
}

function useTransportServiceProvider(
	transportServiceProviderId?: string,
	shouldFetch = false
) {
	const xhr = useXHR()
	const {
		data: transportServiceProvider,
		mutate: fetchTransportServiceProvider,
		error,
	} = useSWR(
		shouldFetch && transportServiceProviderId
			? `/api/transport-service-providers/${transportServiceProviderId}`
			: null,
		() => XHR(xhr).getTransportServiceProvider(transportServiceProviderId || "")
	)
	const isFetching = !transportServiceProvider && !error
	return {
		isFetching,
		transportServiceProvider,
		error,
		fetchTransportServiceProvider,
	}
}

export function TransportServiceProviderDetails({
	transportServiceProviderId,
	children,
}: {
	transportServiceProviderId: string
	children?: (props: {
		transportServiceProvider: ITransportServiceProvider
		revalidate: () => void
	}) => React.ReactNode
}) {
	const {
		transportServiceProvider,
		isFetching,
		fetchTransportServiceProvider,
	} = useTransportServiceProvider(transportServiceProviderId.toString(), true)
	const { hasPermission } = useCheckPermissions()
	if (isFetching && !transportServiceProvider)
		return <Spinner alignCenter padding="4" />
	if (!transportServiceProvider)
		return <Box>Transport service provider does not exists</Box>
	const { name } = transportServiceProvider
	return (
		<Box>
			<Breadcrumbs
				title={name}
				items={[[generatePath("/transport-service-providers"), "Suppliers"]]}
			/>
			<Box>
				<Tabs layout="col" gap="0">
					<TabsList>
						<NavLink
							to={generatePath(
								"/transport-service-providers/:transportServiceProviderId",
								{
									transportServiceProviderId,
								}
							)}
							end
						>
							Details
						</NavLink>
						{transportServiceProvider.supplies_cabs ? (
							<NavLink
								to={generatePath(
									"/transport-service-providers/:transportServiceProviderId/bookings",
									{
										transportServiceProviderId,
									}
								)}
							>
								Cab Bookings
							</NavLink>
						) : null}
						{transportServiceProvider.supplies_travel_activities ? (
							<NavLink
								to={generatePath(
									"/transport-service-providers/:transportServiceProviderId/travel-activity-bookings",
									{
										transportServiceProviderId,
									}
								)}
							>
								Activity Bookings
							</NavLink>
						) : null}
						<NavLink
							to={generatePath(
								"/transport-service-providers/:transportServiceProviderId/payments",
								{
									transportServiceProviderId,
								}
							)}
						>
							Payments
						</NavLink>
						{hasPermission(PERMISSIONS.MANAGE_ACCOUNTING) ? (
							<NavLink
								to={generatePath(
									"/transport-service-providers/:transportServiceProviderId/accounting",
									{
										transportServiceProviderId,
									}
								)}
							>
								Accounting
							</NavLink>
						) : null}
					</TabsList>
					<TabContent>
						{children
							? children({
									transportServiceProvider,
									revalidate: fetchTransportServiceProvider,
								})
							: null}
					</TabContent>
				</Tabs>
			</Box>
		</Box>
	)
}

export function TransportServiceProviderBasicDetails({
	transportServiceProvider,
	onChange,
}: {
	transportServiceProvider: ITransportServiceProvider
	onChange: (transportServiceProviderId: number) => void
}) {
	const {
		contacts,
		drivers,
		name,
		solo_driver,
		supplies_cabs,
		supplies_travel_activities,
		id,
	} = transportServiceProvider
	const xhr = useXHR()
	const hasTravelActivities = useHasFeatureFlag("travel_activities")
	return (
		<Box>
			<Container paddingY="4" bgColor="default" borderBottomWidth="1">
				<Inline justifyContent={"between"}>
					<Stack gap="2">
						<Heading as="h2" fontSize="xl">
							{name}
						</Heading>
						<Text as="span" color="muted">
							{joinAttributes(
								solo_driver ? (
									<>
										<Icons.Driver title="Single Driver" /> Solo Driver
									</>
								) : null,
								supplies_cabs && hasTravelActivities ? (
									<>
										<Icons.Taxi title="Cab Supplier" /> Cabs
									</>
								) : null,
								supplies_travel_activities ? (
									<>
										<Icons.Ticket title="Travel Activity Supplier" /> Travel
										Activities
									</>
								) : null
							)}
						</Text>
					</Stack>
					<Box>
						<UpdateItemInDialog
							transportServiceProvider={transportServiceProvider}
							onSuccess={() => onChange(id)}
						>
							{({ edit }) => (
								<Button onClick={() => edit()} size="sm">
									<Icons.Pencil /> Edit
								</Button>
							)}
						</UpdateItemInDialog>
					</Box>
				</Inline>
			</Container>
			<Box
				paddingY="8"
				paddingX="4"
				bgColor="default"
				roundedBottom="lg"
				borderLeftWidth="1"
				borderRightWidth="1"
				borderBottomWidth="1"
			>
				<Grid gap="6" paddingTop={"4"}>
					<Col sm={12} md={6}>
						<ContactsList
							contacts={contacts}
							phoneNumberRequired
							onCreate={async (data) => {
								return XHR(xhr)
									.createContact(transportServiceProvider.id, data)
									.then((contact) => {
										onChange(transportServiceProvider.id)
										return contact
									})
							}}
							onChange={() => onChange(transportServiceProvider.id)}
						/>
					</Col>
					<Col>
						{supplies_cabs ? (
							<DriversList
								transportServiceProvider={transportServiceProvider}
								drivers={drivers}
								onChange={() => onChange(transportServiceProvider.id)}
							/>
						) : null}
					</Col>
				</Grid>
			</Box>
		</Box>
	)
}

type TBookingStatus = "all" | "booked" | "assigned_but_not_booked"

type TFilters = TSearchParams & {
	pickup_after?: Date
	pickup_before?: Date
	trips_start_date_after?: Date
	trips_start_date_before?: Date
	status?: TBookingStatus
	drivers?: Array<TDriver>
}

type TFiltersInLocationUrl = TSearchParams & {
	pickup_after?: string
	pickup_before?: string
	trips_start_date_after?: string
	trips_start_date_before?: string
	status?: TBookingStatus
	drivers?: Array<string>
}

function filtersToUrl(filters: TFilters): TFiltersInLocationUrl {
	const {
		pickup_after,
		pickup_before,
		page,
		q,
		status,
		drivers,
		trips_start_date_after,
		trips_start_date_before,
	} = filters
	const filtersInLocation: TFiltersInLocationUrl = {}
	if (q) {
		filtersInLocation.q = q
	}
	if (status) {
		filtersInLocation.status = status
	}
	if (page) {
		filtersInLocation.page = page
	}
	if (pickup_after) {
		filtersInLocation.pickup_after = dateToQuery(pickup_after)
	}
	if (pickup_before) {
		filtersInLocation.pickup_before = dateToQuery(pickup_before)
	}
	if (trips_start_date_after) {
		filtersInLocation.trips_start_date_after = dateToQuery(
			trips_start_date_after
		)
	}
	if (trips_start_date_before) {
		filtersInLocation.trips_start_date_before = dateToQuery(
			trips_start_date_before
		)
	}
	if (drivers) {
		filtersInLocation.drivers = drivers.map((d) => `${d.id}#${d.name}`)
	}
	return filtersInLocation
}

function filtersFromUrl(filters: TFiltersInLocationUrl): TFilters {
	const {
		pickup_after,
		pickup_before,
		q,
		page,
		status,
		drivers,
		trips_start_date_after,
		trips_start_date_before,
	} = filters
	const params: TFilters = {}
	if (q) {
		params.q = q
	}
	if (page) {
		params.page = page
	}
	if (status) {
		params.status = status
	}
	if (pickup_after) {
		params.pickup_after = parseDateFromQuery(pickup_after)
	}
	if (pickup_before) {
		params.pickup_before = parseDateFromQuery(pickup_before)
	}
	if (trips_start_date_after) {
		params.trips_start_date_after = parseDateFromQuery(trips_start_date_after)
	}
	if (trips_start_date_before) {
		params.trips_start_date_before = parseDateFromQuery(trips_start_date_before)
	}
	if (drivers) {
		params.drivers = drivers.map((d) => {
			const [id, ...name] = d.split("#")
			return {
				id,
				name: name.join("#"),
			} as never as TDriver
		})
	}
	return params
}

function filtersToRequestParams(filters: TFilters) {
	const {
		pickup_after,
		pickup_before,
		page,
		q,
		status,
		drivers,
		trips_start_date_after,
		trips_start_date_before,
	} = filters
	const params: {
		status?: string
		page?: number
		q?: string
		pickup_after?: string
		pickup_before?: string
		trips_start_date_after?: string
		trips_start_date_after_local?: string
		trips_start_date_before?: string
		trips_start_date_before_local?: string
		start_date?: string
		end_date?: string
		booked?: 0 | 1
		drivers?: Array<number>
	} = { status }
	if (page) {
		params.page = page
	}
	if (q) {
		params.q = q
	}
	if (status) {
		params.status = status
	}
	if (pickup_after) {
		params.pickup_after = dateToUTCString(pickup_after)
		params.start_date = dateToUTCString(pickup_after)
	}
	if (pickup_before) {
		params.pickup_before = dateToUTCString(endOf(pickup_before, "day"))
		params.end_date = dateToUTCString(endOf(pickup_before, "day"))
	}
	if (trips_start_date_after) {
		params.trips_start_date_after = dateToUTCString(trips_start_date_after)
		params.trips_start_date_after_local = formatDate(
			trips_start_date_after,
			"YYYY-MM-DD"
		)
	}
	if (trips_start_date_before) {
		params.trips_start_date_before = dateToUTCString(
			endOf(trips_start_date_before, "day")
		)
		params.trips_start_date_before_local = formatDate(
			trips_start_date_before,
			"YYYY-MM-DD"
		)
	}
	if (drivers?.length) {
		params.drivers = drivers.map((d) => d.id)
	}
	return params
}

export function TransportServiceProviderBookings({
	transportServiceProvider,
}: {
	transportServiceProvider: ITransportServiceProvider
}) {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToUrl,
		fromQuery: filtersFromUrl,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
		status: query.status || "booked",
	})
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Box>
			<Search
				initialParams={params}
				placeholder="Search by Guest, Services etc..."
				title="Cab Schedules"
				resetParams={(params) => ({
					q: "",
					status: params.status,
				})}
				Filters={Filters}
				actions={
					<Inline gap={"2"}>
						<Button
							as={XHRLink}
							href="/scheduled-cabs/export"
							title="Download Report as Excel/CSV File"
							query={{
								...filtersToRequestParams(params),
								transportServiceProviders: [transportServiceProvider.id],
								timezone_offset: config.timezoneOffset,
							}}
							download
						>
							<Icons.DocumentDownload /> Excel/CSV
						</Button>
						<ButtonLink
							to={`/transport-service-providers/${transportServiceProvider.id}/bookings/trip-wise-booking`}
						>
							Trip Wise
						</ButtonLink>
					</Inline>
				}
				areAdvancedFiltersApplied={(filters) => {
					const { status, ...otherParams } = filters
					return Object.keys(otherParams).length > 0
				}}
				onSearch={(newParams) => {
					setParams({ ...newParams, page: 1 })
				}}
			>
				{({ searchParams, setSearchParams }) => (
					<Tabs layout="col" gap={"0"}>
						<TabsList>
							<TabItem
								active={searchParams.status === "booked"}
								onClick={() => {
									setSearchParams({ ...searchParams, status: "booked" })
								}}
							>
								Booked
							</TabItem>
							<TabItem
								active={searchParams.status === "assigned_but_not_booked"}
								onClick={() => {
									setSearchParams({
										...searchParams,
										status: "assigned_but_not_booked",
									})
								}}
							>
								Not Booked
							</TabItem>
							<TabItem
								active={searchParams.status === "all"}
								onClick={() => {
									setSearchParams({ ...searchParams, status: "all" })
								}}
							>
								All
							</TabItem>
						</TabsList>
						<TabContent>
							<ListView<IScheduledCab, TFilters>
								pageKey={`transport-service-providers/${transportServiceProvider.id}/bookings`}
								fetch={(xhr, filters) =>
									XHR(xhr).getBookings({
										...filtersToRequestParams(filters),
										transportServiceProviders: [transportServiceProvider.id],
									})
								}
								onPageChange={(page) => setParams({ ...params, page })}
								params={params}
							>
								{({ items }) => (
									<Table
										bordered
										hover
										responsive
										headers={[
											"Service",
											"Date",
											"Cab Type",
											"Cab",
											"Driver",
											"Guest",
											"Amount",
										]}
										alignCols={{ 6: "right" }}
										rows={items.map(
											({
												currency,
												booked,
												booked_price,
												cab_type,
												cab,
												driver,
												cab_schedule,
												trip,
												latest_booking_stage,
												payments_amount,
												paid_payments_amount,
												total_payments_amount,
												refunding_payments_amount,
												paid_refunding_payments_amount,
											}) => [
												cab_schedule ? (
													<Box>
														<Box>
															<Link
																color="accent"
																fontWeight="semibold"
																to={generatePath("/cab-schedules/:scheduleId", {
																	scheduleId: cab_schedule.id.toString(),
																})}
																anchored
															>
																{cab_schedule.transport_service.short_name}
															</Link>
														</Box>
														<Box fontSize="sm" color="muted">
															{cab_schedule.transport_service.service}
														</Box>
													</Box>
												) : null,
												cab_schedule ? (
													<Box>
														<Box>
															<Time
																timestamp={cab_schedule.start_date}
																localTimestamp={cab_schedule.start_date_local}
															/>
														</Box>
														<Box fontSize="sm" color="muted">
															{getStartDateTimeOfCabSchedule(cab_schedule) ? (
																<Time
																	value={getStartDateTimeOfCabSchedule(
																		cab_schedule
																	)}
																	format="HH:mm"
																/>
															) : null}
														</Box>
													</Box>
												) : null,
												<Box>
													<Box>{cab_type.name}</Box>
													<Box fontSize="sm" color="muted">
														{latest_booking_stage.title}
													</Box>
												</Box>,
												cab ? (
													<Box>
														<Box>{cab.name}</Box>
														<Box fontSize="sm" color="muted">
															{cab.number_plate}
														</Box>
													</Box>
												) : null,
												driver ? (
													<Box>
														<Box>{driver.name}</Box>
														<Box fontSize="sm" color="muted">
															{joinAttributes(
																driver.phone_number ? (
																	<PhoneNumber
																		value={driver.phone_number}
																		iconOnly
																	/>
																) : null,
																driver.email ? (
																	<Email value={driver.email} iconOnly />
																) : null
															)}
														</Box>
													</Box>
												) : null,
												trip ? (
													<Box>
														<Box>
															<Link
																to={generatePath("/trips/:tripId", {
																	tripId: trip.id.toString(),
																})}
																color="accent"
															>
																{trip.contact.name}
															</Link>
														</Box>
														<Box fontSize="sm" color="muted">
															{joinAttributes(
																trip.id,
																trip.contact.phone_numbers?.length ? (
																	<PhoneNumber
																		value={trip.contact.phone_numbers}
																		iconOnly
																	/>
																) : null,
																trip.contact.email ? (
																	<Email value={trip.contact.email} iconOnly />
																) : null
															)}
														</Box>
													</Box>
												) : null,
												booked_price !== undefined && booked_price !== null ? (
													<Box>
														<Box>
															{isNumeric(booked_price) ? (
																<Inline
																	fontWeight="semibold"
																	alignItems="center"
																	justifyContent="end"
																	gap="1"
																>
																	{refunding_payments_amount === null ? (
																		// If not in refund mode, show Green Check when
																		// - booked
																		// - or, when booking with no paid instalments is dropped without cancellation charges
																		// AND, when all payments are paid.
																		// In case payments_amount is null, paid_payments_amount must also be null.
																		booked &&
																		paid_payments_amount === payments_amount ? (
																			<Box display="inlineBlock" marginLeft="1">
																				<Icons.OkCircleSolid color="success" />
																			</Box>
																		) : null
																	) : paid_refunding_payments_amount ===
																	  refunding_payments_amount ? (
																		// If in refund mode, show Green Check when
																		// - Refund is complete
																		<Box display="inlineBlock" marginLeft="1">
																			<Icons.OkCircleSolid color="success" />
																		</Box>
																	) : null}
																	<Text>Booking:</Text>
																	{latest_booking_stage.is_same_or_after_booked ? (
																		<Money
																			showCurrency
																			amount={total_payments_amount || 0}
																			currency={currency}
																		/>
																	) : isNumeric(booked_price) ? (
																		<Box>
																			<Money
																				showCurrency
																				amount={booked_price}
																				currency={currency}
																			/>
																		</Box>
																	) : null}
																</Inline>
															) : null}
															{isNumeric(paid_payments_amount) ? (
																<Box
																	fontSize="sm"
																	fontWeight="semibold"
																	title={`${formatMoneyByIntl(moneyParseByDecimal(paid_payments_amount, currency))} Paid`}
																	marginTop="1"
																	color="success"
																>
																	Paid:{" "}
																	<Money
																		amount={paid_payments_amount}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : //show paid=0 when "paid_payments_amount" is null && "payments_amount" is not null & "payments_amount" is greater than 0
															isNumeric(payments_amount) &&
															  Number(payments_amount || 0) > 0 ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="muted"
																>
																	Paid:{" "}
																	<Money
																		amount={0}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
															{isNumeric(refunding_payments_amount) ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="warning"
																	title="Refund"
																	whiteSpace="nowrap"
																>
																	Refund:{" "}
																	<Money
																		amount={paid_refunding_payments_amount || 0}
																		currency={currency}
																		showCurrency
																	/>
																	{" / "}
																	<Money
																		amount={refunding_payments_amount}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
														</Box>
													</Box>
												) : null,
											]
										)}
									/>
								)}
							</ListView>
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Box>
	)
}

export function TransportServiceProviderTravelActivityBookings({
	transportServiceProvider,
}: {
	transportServiceProvider: ITransportServiceProvider
}) {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToUrl,
		fromQuery: filtersFromUrl,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
		status: query.status || "booked",
	})
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Box paddingTop="8">
			<Search
				initialParams={params}
				placeholder="Search by Guest, Services etc..."
				title="Travel Activity Bookings"
				resetParams={(params) => ({
					q: "",
					status: params.status,
				})}
				Filters={Filters}
				actions={
					<>
						<Button
							as={XHRLink}
							href="/travel-activity-bookings/download"
							title="Download Report as Excel/CSV File"
							query={{
								...filtersToRequestParams(params),
								suppliers: [transportServiceProvider.id],
								timezone_offset: config.timezoneOffset,
							}}
							download
						>
							<Icons.DocumentDownload /> Excel/CSV
						</Button>
					</>
				}
				areAdvancedFiltersApplied={(filters) => {
					const { status, ...otherParams } = filters
					return Object.keys(otherParams).length > 0
				}}
				onSearch={(newParams) => {
					setParams({ ...newParams, page: 1 })
				}}
			>
				{({ searchParams, setSearchParams }) => (
					<Tabs>
						<TabsList>
							<TabItem
								active={searchParams.status === "booked"}
								onClick={() => {
									setSearchParams({ ...searchParams, status: "booked" })
								}}
							>
								Booked
							</TabItem>
							<TabItem
								active={searchParams.status === "assigned_but_not_booked"}
								onClick={() => {
									setSearchParams({
										...searchParams,
										status: "assigned_but_not_booked",
									})
								}}
							>
								Not Booked
							</TabItem>
							<TabItem
								active={searchParams.status === "all"}
								onClick={() => {
									setSearchParams({ ...searchParams, status: "all" })
								}}
							>
								All
							</TabItem>
						</TabsList>
						<TabContent>
							<ListView<TTravelActiviytBooking, TFilters>
								pageKey={`transport-service-providers/${transportServiceProvider.id}/travel-activity-bookings`}
								fetch={(xhr, filters) =>
									XHR(xhr).getTravelActivityBookings({
										...filtersToRequestParams(filters),
										suppliers: [transportServiceProvider.id],
									})
								}
								onPageChange={(page) => setParams({ ...params, page })}
								params={params}
							>
								{({ items }) => (
									<Table
										bordered
										hover
										responsive
										headers={["Service", "Date", "Ticket", "Guest", "Amount"]}
										alignCols={{ 4: "right" }}
										rows={items.map(
											({
												currency,
												booked,
												booked_price,
												trip,
												payments_amount,
												paid_payments_amount,
												total_payments_amount,
												refunding_payments_amount,
												paid_refunding_payments_amount,
												activity,
												ticket_type,
												ticket_tourist_configurations_short_name,
												start_date,
												start_date_local,
												start_time_formatted,
												duration_formatted,
												is_booked,
												dropped_at,
											}) => [
												<Box>
													<Link
														to={generatePath(
															"/travel-activities/:travelActivityId",
															{
																travelActivityId: activity.id.toString(),
															}
														)}
														color="accent"
														anchored
													>
														{activity.name}
														{ticket_type ? ` - ${ticket_type.name}` : ``}
													</Link>
												</Box>,
												<Box>
													<Box>
														<Time
															timestamp={start_date}
															localTimestamp={start_date_local}
														/>
													</Box>
													{start_time_formatted || duration_formatted ? (
														<Box fontSize="sm" color="muted">
															{joinAttributes(
																start_time_formatted,
																duration_formatted
															)}
														</Box>
													) : null}
												</Box>,
												<Box>
													<Box>{ticket_tourist_configurations_short_name}</Box>
													<Box fontSize="sm" color="muted">
														{is_booked ? (
															<>
																<Icons.OkCircleSolid color="success" /> Booked
															</>
														) : dropped_at ? (
															<>
																<Icons.Ban color="warning" /> Dropped
															</>
														) : (
															"Pending"
														)}
													</Box>
												</Box>,
												trip ? (
													<Box>
														<Box>
															<Link
																to={generatePath("/trips/:tripId", {
																	tripId: trip.id.toString(),
																})}
																color="accent"
															>
																{trip.contact.name}
															</Link>
														</Box>
														<Box fontSize="sm" color="muted">
															{joinAttributes(
																`Trip#` + trip.id,
																trip.contact.phone_numbers?.length ? (
																	<PhoneNumber
																		value={trip.contact.phone_numbers}
																		iconOnly
																	/>
																) : null,
																trip.contact.email ? (
																	<Email value={trip.contact.email} iconOnly />
																) : null
															)}
														</Box>
													</Box>
												) : null,
												booked_price !== undefined && booked_price !== null ? (
													<Box>
														{isNumeric(booked_price) ? (
															<Inline
																fontWeight="semibold"
																justifyContent="end"
																gap="1"
															>
																{refunding_payments_amount === null ? (
																	booked &&
																	paid_payments_amount === payments_amount ? (
																		<Box display="inlineBlock" marginLeft="1">
																			<Icons.OkCircleSolid color="success" />
																		</Box>
																	) : null
																) : paid_refunding_payments_amount ===
																  refunding_payments_amount ? (
																	<Box display="inlineBlock" marginLeft="1">
																		<Icons.OkCircleSolid color="success" />
																	</Box>
																) : null}
																<Text>Booking:</Text>
																{booked ? (
																	<Money
																		amount={total_payments_amount || 0}
																		currency={currency}
																		showCurrency
																	/>
																) : isNumeric(booked_price) ? (
																	<Box>
																		<Money
																			amount={booked_price}
																			currency={currency}
																			showCurrency
																		/>
																	</Box>
																) : null}
															</Inline>
														) : null}
														{isNumeric(paid_payments_amount) ? (
															<Box
																fontWeight="semibold"
																fontSize="sm"
																title={`${formatMoneyByIntl(moneyParseByDecimal(paid_payments_amount, currency))} Paid`}
																color="success"
															>
																Paid:{" "}
																<Money
																	amount={paid_payments_amount}
																	currency={currency}
																	showCurrency
																/>
															</Box>
														) : //show paid=0 when "paid_payments_amount" is null && "payments_amount" is not null & "payments_amount" is greater than 0
														isNumeric(payments_amount) &&
														  Number(payments_amount || 0) > 0 ? (
															<Box
																fontWeight="semibold"
																fontSize="sm"
																color="muted"
															>
																Paid:{" "}
																<Money
																	amount={0}
																	currency={currency}
																	showCurrency
																/>
															</Box>
														) : null}
														{isNumeric(refunding_payments_amount) ? (
															<Box
																fontWeight="semibold"
																fontSize="sm"
																color="warning"
																title="Refund"
																whiteSpace="nowrap"
															>
																Refund:{" "}
																<Money
																	amount={paid_refunding_payments_amount || 0}
																	currency={currency}
																	showCurrency
																/>
																{" / "}
																<Money
																	amount={refunding_payments_amount}
																	currency={currency}
																	showCurrency
																/>
															</Box>
														) : null}
													</Box>
												) : null,
											]
										)}
									/>
								)}
							</ListView>
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Box>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<DatePickerField label="Start Date" name="pickup_after" />
			<DatePickerField label="End Date" name="pickup_before" />
		</Stack>
	)
}

export function TransportServiceProviderPayments({
	transportServiceProviderId,
}: {
	transportServiceProviderId: number | string
}) {
	const transportServiceProviders = useMemo(() => {
		if (!transportServiceProviderId) return []
		return [Number(transportServiceProviderId)]
	}, [transportServiceProviderId])
	if (!transportServiceProviderId) return null
	return (
		<Box paddingTop="8">
			<PaymentsList
				title="Payments"
				transportServiceProviders={transportServiceProviders}
				debit
				hideContact
			/>
		</Box>
	)
}

export function TransportServiceProviderAccountings({
	transportServiceProvider,
}: {
	transportServiceProvider: ITransportServiceProvider
}) {
	return (
		<Box marginY="8">
			{transportServiceProvider.accounting_accounts?.length ? (
				<Container>
					<Box textAlign="right" marginBottom="2">
						<ButtonLink
							to={generatePath(
								"/transport-service-providers/:transportServiceProviderId/payments",
								{
									transportServiceProviderId: String(
										transportServiceProvider.id
									),
								}
							)}
						>
							View Due Payments <Icons.ChevronDown rotate="270" />
						</ButtonLink>
					</Box>
					<AccountsList
						accounts={transportServiceProvider.accounting_accounts}
					/>
				</Container>
			) : (
				<Container>
					<Stack gap="4">
						<Text fontSize="md" fontWeight="semibold">
							No accounting activities for this service provider
						</Text>
						<Text color="muted">
							Log payments regarding bookings or services of this service
							provider to manage the accounting.
						</Text>
						<Box>
							<ButtonLink
								to={generatePath(
									"/transport-service-providers/:transportServiceProviderId/payments",
									{
										transportServiceProviderId: String(
											transportServiceProvider.id
										),
									}
								)}
								status="primary"
							>
								View Due Payments <Icons.ChevronDown rotate="270" />
							</ButtonLink>
						</Box>
					</Stack>
				</Container>
			)}
		</Box>
	)
}

export function SelectSupplierContact({
	supplierId,
	onCreateSuccess,
	...props
}: Omit<React.ComponentProps<typeof SelectContact>, "onCreate" | "options"> & {
	supplierId: number | string
	onCreateSuccess?: (contact: IContact) => void
}) {
	const xhr = useXHR()
	const { data, mutate: revalidate } = useSWR(
		`/transport-service-providers/${supplierId}?select-contact`,
		() => XHR(xhr).getTransportServiceProvider(supplierId.toString())
	)
	return (
		<>
			<SelectContact
				{...props}
				phoneNumberRequired
				options={data?.contacts?.filter((c) => !c.deleted_at) || []}
				onCreate={async (data) => {
					const contact = await XHR(xhr).createContact(supplierId, data)
					revalidate()
					onCreateSuccess?.(contact)
					return contact
				}}
			/>
		</>
	)
}

function TripWiseBookingsFilters() {
	return (
		<Stack gap="4">
			<DateRangePickerField
				fromName="trips_start_date_after"
				toName="trips_start_date_before"
				label="Trip Between"
			/>
			<SelectInputField label="Status" name="status">
				<option value="">Select Status</option>
				<option value="booked">Fully Booked</option>
				<option value="partially_booked">Partially Booked</option>
				<option value="not_booked">Not Booked</option>
			</SelectInputField>
		</Stack>
	)
}
export function TSPTripWiseBookings({
	transportServiceProvider,
}: {
	transportServiceProvider: ITransportServiceProvider
}) {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToUrl,
		fromQuery: filtersFromUrl,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
	})
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Box>
			<Search
				initialParams={params}
				placeholder="Search by Guest, Trip Id etc..."
				title="Trip Wise Cab Schedules"
				Filters={TripWiseBookingsFilters}
				actions={
					<Inline gap={"2"}>
						<TemporaryStorage
							initUrl={`/trip-wise-cab-schedule/download`}
							initParams={{
								...filtersToRequestParams(params),
								supplier: transportServiceProvider.id,
							}}
						>
							{({ generate, url, message, reset, isGenerating }) =>
								url ? (
									<Button
										as="a"
										href={url}
										target="_blank"
										download
										level="secondary"
										onClick={() => reset()}
									>
										{message}
									</Button>
								) : (
									<Button
										onClick={generate}
										disabled={isGenerating}
										level="secondary"
									>
										{message || (
											<>
												<Icons.DocumentDownload /> Excel/CSV
											</>
										)}
									</Button>
								)
							}
						</TemporaryStorage>
						<ButtonLink
							to={`/transport-service-providers/${transportServiceProvider.id}/bookings`}
						>
							Cab Schedules
						</ButtonLink>
					</Inline>
				}
				areAdvancedFiltersApplied={(filters) => {
					const { ...otherParams } = filters
					return Object.keys(otherParams).length > 0
				}}
				onSearch={(newParams) => {
					setParams({ ...newParams, page: 1 })
				}}
			>
				<ListView<ITSPTripWiseCabsSchedule, TFilters>
					pageKey={`transport-service-providers/${transportServiceProvider.id}/trip-wise-booking`}
					fetch={(xhr, filters) =>
						XHR(xhr).getTripWiseBookings({
							...filtersToRequestParams(filters),
							supplier: transportServiceProvider.id,
						})
					}
					onPageChange={(page) => setParams({ ...params, page })}
					params={params}
				>
					{({ items }) => (
						<Table
							bordered
							hover
							responsive
							headers={[
								"Trip Info",
								"Service Dates",
								"First Service",
								"Last Service",
								"Cabs Type/Cabs",
								"Driver",
								"Amount",
								"",
							]}
							alignCols={{ 6: "right", 7: "right" }}
							rows={items.map((item) => {
								const { contact, id, schedules } = item

								const bookingPrices: TMoney[] = []
								const givenPrices: TMoney[] = []
								const refundAmounts: TMoney[] = []
								const paidRefundAmount: TMoney[] = []
								let isTotalPaid = true

								const driversOnTrip = new Map<string, TDriver[]>()
								const cabsOnTrip = new Map()

								let allBooked = true
								let allUnbooked = true
								let cabTypesCount = 0
								let driverCount = 0

								const grouped: Record<string, ICabSchedule[]> = {}

								for (const sch of schedules) {
									const cabsPerService = new Map()
									const driversPerService = new Map<string, TDriver>()

									const key = sch.start_date
									if (!grouped[key]) grouped[key] = []
									grouped[key].push(sch)

									for (const cab of sch.cabs) {
										if (cab.booked) {
											allUnbooked = false
										} else {
											allBooked = false
										}

										// Process cabs per service
										if (cabTypesCount < 3) {
											const cabKey = cab.cab ? cab.cab.name : cab.cab_type.name
											const existing = cabsPerService.get(cabKey) ?? {
												...(cab.cab || cab.cab_type),
												count: 0,
											}
											existing.count += 1
											cabsPerService.set(cabKey, existing)
										}

										// Process drivers per service
										if (cab.driver?.name && driverCount < 3) {
											driversPerService.set(cab.driver.name, cab.driver)
										}

										// Process payment amounts
										if (cab.booked_price_money)
											bookingPrices.push(cab.booked_price_money)
										if (cab.paid_payments_amount_money)
											givenPrices.push(cab.paid_payments_amount_money)
										if (cab.refunding_payments_amount_money)
											refundAmounts.push(cab.refunding_payments_amount_money)
										if (cab.paid_refunding_payments_amount_money)
											paidRefundAmount.push(
												cab.paid_refunding_payments_amount_money
											)
										if (
											numberToLocalString(cab.booked_price) >
											numberToLocalString(cab.paid_payments_amount)
										) {
											isTotalPaid = false
										}
									}

									// Set for cabs per trip
									if (cabTypesCount < 3) {
										const cabKeyStr = Array.from(cabsPerService.values())
											.map((cab) => `${cab.count} ${cab.name}`)
											.join(" + ")
										if (!cabsOnTrip.has(cabKeyStr)) {
											cabTypesCount += cabsPerService.size
										}
										cabsOnTrip.set(cabKeyStr, [...cabsPerService.values()])
									}

									// Set for drivers per trip
									if (driverCount < 3 && driversPerService.size > 0) {
										const driversKey = Array.from(driversPerService.values())
											.map((driver) => driver.name)
											.join(" + ")
										if (!driversOnTrip.has(driversKey)) {
											driverCount += driversPerService.size
										}
										driversOnTrip.set(driversKey, [
											...driversPerService.values(),
										])
									}
								}

								// Determine booking status
								const bookingStatus:
									| "booked"
									| "not_booked"
									| "partially_booked" = allBooked
									? "booked"
									: allUnbooked
										? "not_booked"
										: "partially_booked"

								// Sort grouped schedules by date
								const sortedEntries = Object.entries(grouped).sort(
									([a], [b]) => {
										return new Date(a).getTime() - new Date(b).getTime()
									}
								)

								// Convert to ordered Map
								const dateWiseSchedules = new Map<string, ICabSchedule[]>(
									sortedEntries
								)
								const sortedDateWiseSchedules = Array.from(
									dateWiseSchedules.entries()
								)

								return [
									<Box>
										<Box>
											<Link
												to={generatePath("/trips/:tripId", {
													tripId: id.toString(),
												})}
												color="accent"
											>
												{id}
											</Link>
										</Box>
										<Text fontSize="sm" color="muted">
											{contact.name}
										</Text>
									</Box>,
									<Box>
										<Inline alignItems={"center"} gap="1" flexWrap="wrap">
											<Box>
												<Time
													timestamp={
														sortedDateWiseSchedules[0][1][0].start_date
													}
													localTimestamp={
														sortedDateWiseSchedules[0][1][0].start_date_local
													}
													whiteSpace="nowrap"
													format="DD MMM"
												/>
												{sortedDateWiseSchedules.length > 1 ? (
													<>
														-
														<Time
															timestamp={
																sortedDateWiseSchedules[
																	sortedDateWiseSchedules.length - 1
																][1][0].start_date
															}
															localTimestamp={
																sortedDateWiseSchedules[
																	sortedDateWiseSchedules.length - 1
																][1][0].start_date_local
															}
															whiteSpace="nowrap"
															format="DD MMM"
														/>
													</>
												) : null}
											</Box>

											<Box>
												({sortedDateWiseSchedules.length} Day
												{sortedDateWiseSchedules.length > 1 ? "s" : ""})
											</Box>
										</Inline>
									</Box>,
									<Box>
										<Box>
											<Box fontWeight="semibold">
												{
													sortedDateWiseSchedules[0][1][0].transport_service
														.short_name
												}
											</Box>
											<Box fontSize="sm" color="muted">
												•{" "}
												{
													sortedDateWiseSchedules[0][1][0].transport_service
														.service
												}
											</Box>
										</Box>
									</Box>,
									<Box>
										{sortedDateWiseSchedules.length > 1 ? (
											<Box>
												<Box fontWeight="semibold">
													{
														sortedDateWiseSchedules[
															sortedDateWiseSchedules.length - 1
														][1][0].transport_service.short_name
													}
												</Box>
												<Box fontSize="sm" color="muted">
													•{" "}
													{
														sortedDateWiseSchedules[
															sortedDateWiseSchedules.length - 1
														][1][0].transport_service.service
													}
												</Box>
											</Box>
										) : (
											<Box>-</Box>
										)}
									</Box>,
									<Box>
										<Inline gap={"1"} alignItems={"start"}>
											<Box fontSize="sm">
												{bookingStatus === "booked" ? (
													<Icons.OkCircleSolid color="success" />
												) : bookingStatus === "partially_booked" ? (
													<Icons.OkCircle />
												) : (
													<Icons.XCircle color={"danger"} />
												)}{" "}
											</Box>

											<Stack gap="1" alignItems={"start"}>
												{cabTypesCount < 3 ? (
													Array.from(cabsOnTrip.entries()).map(
														([key, values], idx) => (
															<Inline
																gap={"1"}
																key={idx}
																flexWrap={"wrap"}
																alignItems={"end"}
															>
																{values.map(
																	(
																		cabs: ICab & { count: number },
																		id: number
																	) => (
																		<Inline
																			key={id}
																			gap={"1"}
																			alignItems={"end"}
																		>
																			<Inline
																				key={id}
																				gap={"1"}
																				flexWrap={"wrap"}
																				alignItems={"start"}
																			>
																				<Text whiteSpace="nowrap">
																					{cabs.count} {cabs.name}
																				</Text>{" "}
																				{cabs.number_plate ? (
																					<Badge>{cabs.number_plate}</Badge>
																				) : null}
																				{id !== values.length - 1 ? (
																					<Text fontWeight="semibold"> + </Text>
																				) : null}
																			</Inline>
																		</Inline>
																	)
																)}

																{idx !== cabsOnTrip.size - 1 ? (
																	<Text fontWeight="semibold"> OR </Text>
																) : null}
															</Inline>
														)
													)
												) : (
													<DetailedTripSchedule
														tripWiseBookings={item}
														dateWiseSchedules={sortedDateWiseSchedules}
													>
														{({ onShow }) => (
															<Box
																onClick={onShow}
																cursor="pointer"
																color={"accent"}
															>
																{" "}
																Multiple Cabs
															</Box>
														)}
													</DetailedTripSchedule>
												)}
											</Stack>
										</Inline>
									</Box>,
									<Box>
										{driverCount === 0 ? (
											<Text color="muted">N/A</Text>
										) : driverCount < 3 ? (
											<Inline gap={"1"}>
												<Box>
													{Array.from(driversOnTrip.entries()).map(
														([key, values], idx) => {
															return (
																<Inline
																	key={idx}
																	gap={"1"}
																	alignItems={"end"}
																	flexWrap={"wrap"}
																>
																	{values.map((driver, id) => (
																		<Inline
																			key={id}
																			gap={"1"}
																			alignItems={"end"}
																		>
																			<Text>{driver.name}</Text>
																			{id !== values.length - 1 ? (
																				<Text fontWeight="semibold">+</Text>
																			) : null}
																		</Inline>
																	))}
																	{idx !== driversOnTrip.size - 1 ? (
																		<Box fontWeight="semibold"> OR </Box>
																	) : null}
																</Inline>
															)
														}
													)}
												</Box>
											</Inline>
										) : (
											<Text whiteSpace="nowrap" fontWeight="semibold">
												Multiple Drivers
											</Text>
										)}
									</Box>,
									<Box>
										<BookingAmountSummary
											booking_prices={bookingPrices}
											given_price={givenPrices}
											refund_amount={refundAmounts}
											paid_refund_amount={paidRefundAmount}
											isTotalPaid={isTotalPaid}
										/>
									</Box>,
									<Box>
										<DetailedTripSchedule
											tripWiseBookings={item}
											dateWiseSchedules={sortedDateWiseSchedules}
										>
											{({ onShow }) => (
												<Button onClick={onShow}>
													<Icons.ArrowExpand />
												</Button>
											)}
										</DetailedTripSchedule>
									</Box>,
								]
							})}
						/>
					)}
				</ListView>
			</Search>
		</Box>
	)
}

export function BookingAmountSummary({
	booking_prices,
	given_price,
	refund_amount,
	paid_refund_amount,
	isTotalPaid,
}: {
	booking_prices: TMoney[]
	given_price: TMoney[]
	refund_amount: TMoney[]
	paid_refund_amount: TMoney[]
	isTotalPaid: boolean
}) {
	const totalBooking = addMoneyFromDifferentCurrencies(booking_prices)
	const totalPaid = addMoneyFromDifferentCurrencies(given_price)
	const totalRefund = addMoneyFromDifferentCurrencies(refund_amount)
	const totalPaidRefund = addMoneyFromDifferentCurrencies(paid_refund_amount)
	function moneyArraysEqual(a: TMoney[], b: TMoney[]) {
		if (a.length !== b.length) return false
		const normalize = (arr: TMoney[]) =>
			arr
				.map((m) => `${m.currency}:${m.amount}`)
				.sort()
				.join("|")
		return normalize(a) === normalize(b)
	}

	return (
		<Stack alignItems="end">
			{totalBooking.length > 0 ? (
				<Inline
					fontWeight="semibold"
					gap="1"
					alignItems="center"
					justifyContent="end"
				>
					{totalRefund.length === 0 ? (
						isTotalPaid ? (
							<Box display="inlineBlock" marginLeft="1">
								<Icons.OkCircleSolid color="success" />
							</Box>
						) : null
					) : moneyArraysEqual(totalRefund, totalPaidRefund) ? (
						<Box display="inlineBlock" marginLeft="1">
							<Icons.OkCircleSolid color="success" />
						</Box>
					) : null}
					<Text>Booking:</Text>
					<MoneySum money={totalBooking} />
				</Inline>
			) : null}

			{totalPaid.length > 0 ? (
				<Box fontWeight="semibold" fontSize="sm" color="success">
					Paid: <MoneySum money={totalPaid} />
				</Box>
			) : totalBooking.length > 0 ? (
				<Box fontWeight="semibold" fontSize="sm" color="muted">
					Paid: <MoneySum money={[]} />
				</Box>
			) : null}

			{totalRefund.length > 0 ? (
				<Box
					fontWeight="semibold"
					fontSize="sm"
					color="warning"
					title="Refund"
					whiteSpace="nowrap"
				>
					Refund: <MoneySum money={totalPaidRefund || 0} />
					{" / "}
					<MoneySum money={totalRefund || 0} />
				</Box>
			) : null}
		</Stack>
	)
}

function DetailedTripSchedule({
	tripWiseBookings,
	dateWiseSchedules,
	children,
}: {
	tripWiseBookings: ITSPTripWiseCabsSchedule
	dateWiseSchedules: Array<[string, ICabSchedule[]]>
	children: (props: { onShow: () => void }) => React.ReactNode
}) {
	const { days, start_date, pax_string, nights, destinations, contact, id } =
		tripWiseBookings
	const [isOpen, open, close] = useDialog()

	return (
		<>
			{children({ onShow: () => open() })}
			{isOpen ? (
				<Dialog open onClose={close} title="All Cab Schedules" lg>
					<Box padding="4" bgColor={"inset"}>
						<Box paddingX={"2"}>
							<Heading as="h2" fontSize="lg">
								<Link
									to={generatePath("/trips/:tripId", {
										tripId: id.toString(),
									})}
									color="accent"
								>
									<Text as="span" title="Trip ID">
										<Text as="span" opacity="50">
											#
										</Text>
										&nbsp;<Text as="span">{id}</Text>
									</Text>{" "}
									• {contact.name} •{" "}
									{destinations.map((t) => t.name).join(", ")}
								</Link>
							</Heading>

							<Box whiteSpace="preserve">
								<Inline
									alignItems={{ md: "center" }}
									collapseBelow="sm"
									gap="2"
								>
									<Inline gap="2" alignItems="center">
										<Icons.Calendar color="muted" />
										<Box>
											{joinAttributes(
												<Time value={start_date} />,
												<TimeDuration days={days}>
													{nights}N, {days}D
												</TimeDuration>
											)}
										</Box>
									</Inline>
									<Inline gap="2" alignItems="center">
										<Icons.UserGroup color="muted" />
										<Box>{pax_string}</Box>
									</Inline>
								</Inline>
							</Box>

							<Stack gap={"4"} paddingTop={"4"}>
								{dateWiseSchedules.map(
									([date, schedules]: [string, ICabSchedule[]], index) => (
										<Inline
											position="relative"
											gap="4"
											collapseBelow="sm"
											key={index}
										>
											<Box position="sticky" top="0" zIndex="10">
												<Stack
													bgColor="default"
													rounded="lg"
													borderWidth="1"
													padding="4"
													fontWeight="semibold"
													alignItems={"center"}
													style={{ width: "8rem" }}
												>
													<Time
														timestamp={date}
														localTimestamp={schedules[0].start_date_local}
														dateFormat={"DD MMMM"}
														whiteSpace="nowrap"
													/>
													<Time
														timestamp={date}
														localTimestamp={schedules[0].start_date_local}
														dateFormat={"ddd"}
														color={"muted"}
														fontWeight="normal"
														whiteSpace="nowrap"
													/>
												</Stack>
											</Box>

											<Box flex={"1"}>
												<Stack
													gap={"4"}
													bgColor="default"
													rounded="lg"
													borderWidth="1"
													paddingY="2"
												>
													{schedules.map(
														({ transport_service, cabs, id }, index) => (
															<Box
																key={index}
																borderTopWidth={index > 0 ? "1" : undefined}
																paddingTop={index > 0 ? "2" : undefined}
																paddingX={"3"}
															>
																<Inline
																	gap="2"
																	alignItems="start"
																	collapseBelow="md"
																	justifyContent={"between"}
																>
																	<Box paddingBottom={"2"} width={"1/3"}>
																		<Box fontWeight="semibold">
																			{transport_service.short_name}
																		</Box>
																		<Box fontSize="sm" color="muted">
																			• {transport_service.service}
																		</Box>
																	</Box>
																	<Stack width={"full"}>
																		{cabs.map(
																			(
																				{
																					cab,
																					cab_type,
																					latest_booking_stage,
																					booked_price,
																					total_payments_amount,
																					currency,
																					paid_payments_amount,
																					refunding_payments_amount,
																					paid_refunding_payments_amount,
																					driver,
																				},
																				index
																			) => (
																				<Grid
																					gap="2"
																					key={index}
																					borderTopWidth={
																						index > 0 ? "1" : undefined
																					}
																					paddingY="2"
																					paddingRight="2"
																				>
																					<Col>
																						<Inline
																							gap={"1"}
																							alignItems="center"
																							flexWrap={"wrap"}
																						>
																							{latest_booking_stage.name ===
																							"booked" ? (
																								<Icons.OkCircleSolid color="success" />
																							) : (
																								<Icons.XCircle
																									color={"danger"}
																								/>
																							)}
																							{cab ? (
																								<Box>
																									<Box>{cab.name}</Box>
																									<Box
																										fontSize="sm"
																										color="muted"
																									>
																										{cab.number_plate}
																									</Box>
																								</Box>
																							) : (
																								<Box>{cab_type.name}</Box>
																							)}
																						</Inline>
																					</Col>
																					<Col>
																						{" "}
																						{driver ? (
																							<Box>
																								<Stack
																									gap="2"
																									alignItems="start"
																								>
																									<Inline gap="1">
																										<Box>
																											<Icons.SteeringWheel />
																										</Box>
																										<Box>{driver.name}</Box>
																									</Inline>{" "}
																									<Box
																										fontSize="sm"
																										color="muted"
																									>
																										{joinAttributes(
																											driver.phone_number ? (
																												<PhoneNumber
																													value={
																														driver.phone_number
																													}
																													color={"accent"}
																													title="Contact"
																												/>
																											) : null,
																											driver.email ? (
																												<Email
																													value={driver.email}
																													iconOnly
																												/>
																											) : null
																										)}
																									</Box>
																								</Stack>
																							</Box>
																						) : null}
																					</Col>
																					<Col
																						textAlign={"right"}
																						xl={2}
																						lg={2}
																						md={2}
																						sm={2}
																						xs={2}
																					>
																						{booked_price !== undefined &&
																						booked_price !== null ? (
																							<Box>
																								<Box>
																									<Box fontWeight="semibold">
																										{latest_booking_stage.is_same_or_after_booked ? (
																											<Money
																												showCurrency
																												amount={
																													total_payments_amount ||
																													0
																												}
																												currency={currency}
																											/>
																										) : isNumeric(
																												booked_price
																										  ) ? (
																											<Box>
																												<Money
																													showCurrency
																													amount={booked_price}
																													currency={currency}
																												/>
																											</Box>
																										) : null}
																									</Box>
																									{isNumeric(
																										paid_payments_amount
																									) ? (
																										<Box
																											fontSize="sm"
																											title={`${formatMoneyByIntl(
																												moneyParseByDecimal(
																													paid_payments_amount,
																													currency
																												)
																											)} Paid`}
																											marginTop="1"
																											color="success"
																										>
																											<Icons.Ok />
																											<Box
																												display="inlineBlock"
																												marginLeft="px"
																											>
																												<Money
																													amount={
																														paid_payments_amount
																													}
																													currency={currency}
																													showCurrency
																												/>
																											</Box>
																										</Box>
																									) : null}
																									{isNumeric(
																										refunding_payments_amount
																									) ? (
																										<Box
																											fontSize="sm"
																											title={`Refund: ${formatMoneyByIntl(moneyParseByDecimal(paid_refunding_payments_amount || 0, currency))}/${formatMoneyByIntl(moneyParseByDecimal(refunding_payments_amount, currency))}`}
																											marginTop="1"
																											color="warning"
																										>
																											<Icons.Refresh color="warning" />
																											<Box
																												display="inlineBlock"
																												marginLeft="px"
																											>
																												<Money
																													amount={
																														refunding_payments_amount
																													}
																													currency={currency}
																													showCurrency
																												/>
																											</Box>
																										</Box>
																									) : null}
																								</Box>
																							</Box>
																						) : null}
																					</Col>
																				</Grid>
																			)
																		)}
																	</Stack>
																</Inline>
															</Box>
														)
													)}
												</Stack>
											</Box>
										</Inline>
									)
								)}
							</Stack>
						</Box>
					</Box>
				</Dialog>
			) : null}
		</>
	)
}
